/**
 * rb-combined-listing-fix.js
 *
 * FIXES: "Unavailable" add-to-cart button on every combined-listing product
 * (Zach, Marcus, Blanket Shirts, ...).
 *
 * Cause
 * -----
 * Shopify's native Combined Listings adds an EXTRA option to the storefront
 * product object — so `product.options` reads ["Color", "Color", "Size"] and
 * every variant carries three option values. The theme's variant picker only
 * renders the options it knows about (Color + Size = two <fieldset>s), so when
 * VariantRadios matches the selected values against the variant list it
 * compares 2 values to 3-value variants, finds nothing, treats the variant as
 * null, and disables the button with theme.t.unavailable.
 *
 * Verified live 26 Aug: style-zach, style-marcus,
 * blanket-shirt-shades-of-dusk-plaid, blanket-shirt-grassland-salton-plaid all
 * report 3 storefront options vs 2 rendered fieldsets.
 *
 * Fix
 * ---
 * Only on products where that mismatch exists, resolve the variant ourselves by
 * VALUE CONTAINMENT (a variant matches when it contains every selected value,
 * regardless of option position) and then re-assert the correct button state +
 * hidden variant id. A MutationObserver re-applies it if the theme's own
 * picker stomps the label afterwards.
 *
 * Deliberately inert on normal products — if options and fieldsets agree, this
 * script does nothing at all.
 */
(function () {
  'use strict';

  var form, picker, addBtn, productData, active = false;
  var reapplyTimer = null;

  function labelEl() {
    return (addBtn && addBtn.querySelector('.js-product-add-text')) || addBtn;
  }

  function t(key, fallback) {
    try {
      if (window.theme && window.theme.t && window.theme.t[key]) return window.theme.t[key];
    } catch (e) {}
    return fallback;
  }

  /** Values currently selected in the rendered fieldsets. */
  function selectedValues() {
    if (!picker) return [];
    var vals = [];
    picker.querySelectorAll('fieldset').forEach(function (fs) {
      var checked = fs.querySelector('input[type="radio"]:checked');
      if (checked) { vals.push(checked.value); return; }
      var select = fs.querySelector('select');
      if (select && select.value) vals.push(select.value);
    });
    if (!vals.length) {
      var sel = picker.querySelector('select[name="options[]"], select');
      if (sel && sel.value) vals.push(sel.value);
    }
    return vals;
  }

  /**
   * Match by containment rather than position: a variant is the one the
   * shopper picked when every selected value appears in its option list.
   * Handles the duplicated Color option without caring which position the
   * combined-listing option landed in.
   */
  function resolveVariant(values) {
    if (!productData || !values.length) return null;
    var exact = null;
    for (var i = 0; i < productData.variants.length; i++) {
      var v = productData.variants[i];
      var opts = v.options || [];
      var all = true;
      for (var j = 0; j < values.length; j++) {
        if (opts.indexOf(values[j]) === -1) { all = false; break; }
      }
      if (all) {
        // Prefer an available match; otherwise keep the first match.
        if (v.available) return v;
        if (!exact) exact = v;
      }
    }
    return exact;
  }

  function applyState() {
    if (!active || !addBtn) return;

    var variant = resolveVariant(selectedValues());
    if (!variant) return; // nothing better to say than what the theme shows

    var idInput = form.querySelector('input[name="id"]');
    if (idInput && String(idInput.value) !== String(variant.id)) {
      idInput.value = variant.id;
    }

    var lab = labelEl();

    if (variant.available) {
      // IMPORTANT: when the theme can't resolve the variant, pdp-back-in-stock.js
      // sees a null/sold-out variant and flips the button to "Notify me when
      // back" — on sizes that are actually IN STOCK (observed live on
      // style-zach 29x32). So we must override BIS mode here, not defer to it.
      var want = t('add_to_cart', 'Add to cart');
      if (lab && lab.textContent.trim() !== want) lab.textContent = want;
      if (addBtn.classList.contains('is-bis-mode')) {
        addBtn.classList.remove('is-bis-mode');
        addBtn.setAttribute('type', 'submit');
      }
      if (addBtn.hasAttribute('disabled')) addBtn.removeAttribute('disabled');
      addBtn.classList.remove('is-disabled');
      var wrap = addBtn.closest('.js-product-buttons');
      if (wrap) wrap.classList.remove('is-disabled');
      return;
    }

    // Genuinely sold out: hand the label back to pdp-back-in-stock.js so the
    // notify flow keeps working.
    if (addBtn.classList.contains('is-bis-mode')) return;
    var soldOut = t('sold_out', 'Sold out');
    if (lab && lab.textContent.trim() !== soldOut) lab.textContent = soldOut;
  }

  /* ------------------------------------------------ sold-out size marking */

  /**
   * Cross out option values with no stock, using the theme's own
   * `.is-disabled` class (its diagonal-strike CSS is already global — verified
   * on blanket-shirt-seabreeze-mojave-stripe, where X-Large and XX-Large strike
   * correctly).
   *
   * ONLY runs on combined-listing products. Normal products already mark
   * themselves correctly and are left completely alone; the extra storefront
   * option is what stops the theme's own matching from marking these.
   *
   * A fieldset is only marked when every one of its values actually appears in
   * this product's variants. That skips the combined-listing colour row, whose
   * values belong to sibling products and would otherwise all read as sold out.
   */
  function markSoldOut() {
    if (!active) return; // normal products: the theme handles this itself
    if (!productData || !picker) return;
    var variants = productData.variants || [];
    if (!variants.length) return;

    var fieldsets = [].slice.call(picker.querySelectorAll('fieldset'));

    function valuePresent(val) {
      for (var i = 0; i < variants.length; i++) {
        if ((variants[i].options || []).indexOf(val) !== -1) return true;
      }
      return false;
    }

    // Selected values we can safely hold fixed while testing another option.
    var held = [];
    fieldsets.forEach(function (fs) {
      var checked = fs.querySelector('input[type="radio"]:checked');
      var val = checked ? checked.value : null;
      if (val && valuePresent(val)) held.push({ fs: fs, value: val });
    });

    fieldsets.forEach(function (fs) {
      var inputs = [].slice.call(fs.querySelectorAll('input[type="radio"]'));
      if (!inputs.length) return;

      var allPresent = inputs.every(function (i) { return valuePresent(i.value); });
      if (!allPresent) return; // combined-listing colour row — leave alone

      var others = held.filter(function (h) { return h.fs !== fs; })
                       .map(function (h) { return h.value; });

      inputs.forEach(function (input) {
        var combo = others.concat([input.value]);
        var inStock = variants.some(function (v) {
          if (!v.available) return false;
          var opts = v.options || [];
          return combo.every(function (c) { return opts.indexOf(c) !== -1; });
        });

        var label = fs.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(input.id) : input.id) + '"]')
                 || (input.nextElementSibling && input.nextElementSibling.tagName === 'LABEL' ? input.nextElementSibling : null);
        if (!label) return;

        // Stays clickable on purpose — selecting a sold-out size is how the
        // shopper reaches the back-in-stock notify flow.
        label.classList.toggle('is-disabled', !inStock);
        if (!inStock) label.setAttribute('data-rb-sold-out', 'true');
        else label.removeAttribute('data-rb-sold-out');
      });
    });
  }

  function scheduleApply() {
    if (reapplyTimer) clearTimeout(reapplyTimer);
    reapplyTimer = setTimeout(function () {
      if (active) applyState();
      markSoldOut();
    }, 60);
  }

  function detectMismatch() {
    if (!productData || !picker) return false;
    var optionCount = (productData.options || []).length;
    var fieldsets = picker.querySelectorAll('fieldset').length;
    var names = (productData.options || []).map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });
    var hasDuplicateName = names.length !== names.filter(function (n, i) {
      return names.indexOf(n) === i;
    }).length;
    // Only engage where the picker genuinely can't express the variant.
    return (fieldsets > 0 && optionCount > fieldsets) || hasDuplicateName;
  }

  function init() {
    form = document.querySelector('form.product-form');
    if (!form) return;
    picker = document.querySelector('variant-radios, variant-selects');
    if (!picker) return;
    addBtn = form.querySelector('.product-form__cart-submit') || form.querySelector('[name="add"]');
    if (!addBtn) return;

    var handleMeta = document.querySelector('[data-pdp-bis-config]');
    var handle = null;
    if (handleMeta) {
      try { handle = JSON.parse(handleMeta.textContent).product_handle; } catch (e) {}
    }
    if (!handle) {
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      handle = m && m[1];
    }
    if (!handle) return;

    var ready = window.__rsr_product_data
      ? Promise.resolve(window.__rsr_product_data)
      : fetch('/products/' + handle + '.js').then(function (r) { return r.json(); });

    ready.then(function (data) {
      productData = data;
      window.__rsr_product_data = window.__rsr_product_data || data;

      if (!detectMismatch()) return; // normal product — leave everything alone
      active = true;

      applyState();
      markSoldOut();
      picker.addEventListener('change', scheduleApply);

      // The theme's picker re-runs after ours and can restore "Unavailable";
      // watch the button and re-assert.
      var mo = new MutationObserver(scheduleApply);
      mo.observe(addBtn, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'class'] });
    }).catch(function () { /* leave the theme's behaviour untouched */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
